import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';

// Cache for store mapping - shared across all hook instances
let globalStoreMapping = null;
let loadingPromise = null;

/**
 * Hook to access WinTouch store mapping (code → name)
 * Loads mapping once and caches globally for all components
 * 
 * Usage:
 *   const { getStoreName, storeMapping, loading } = useStoreMapping();
 *   const name = getStoreName('NORTESH'); // Returns "Fichet Norteshopping"
 */
export function useStoreMapping() {
  const [storeMapping, setStoreMapping] = useState(globalStoreMapping || []);
  const [loading, setLoading] = useState(!globalStoreMapping);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If already loaded, use cached data
    if (globalStoreMapping) {
      setStoreMapping(globalStoreMapping);
      setLoading(false);
      return;
    }

    // If already loading, wait for existing promise
    if (loadingPromise) {
      loadingPromise.then(data => {
        setStoreMapping(data);
        setLoading(false);
      }).catch(err => {
        setError(err.message);
        setLoading(false);
      });
      return;
    }

    // Load mapping
    const loadMapping = async () => {
      try {
        const res = await axios.get(`${API}/retail-analytics/stores`);
        const stores = res.data.mapping || [];
        globalStoreMapping = stores;
        return stores;
      } catch (err) {
        // If admin-only endpoint fails, try alternative
        console.warn('Failed to load store mapping:', err.message);
        globalStoreMapping = [];
        return [];
      }
    };

    loadingPromise = loadMapping();
    loadingPromise.then(data => {
      setStoreMapping(data);
      setLoading(false);
      loadingPromise = null;
    }).catch(err => {
      setError(err.message);
      setLoading(false);
      loadingPromise = null;
    });
  }, []);

  /**
   * Get store name from WinTouch code
   * @param {string} code - WinTouch store code (e.g., "NORTESH")
   * @returns {string} - Store name or code if not found
   */
  const getStoreName = useCallback((code) => {
    if (!code) return '';
    const store = storeMapping.find(s => 
      s.wintouch_store_code === code || 
      s.wintouch_store_code?.toLowerCase() === code?.toLowerCase()
    );
    return store?.name || code;
  }, [storeMapping]);

  /**
   * Get full store info from WinTouch code
   * @param {string} code - WinTouch store code
   * @returns {object|null} - Store object or null
   */
  const getStoreInfo = useCallback((code) => {
    if (!code) return null;
    return storeMapping.find(s => 
      s.wintouch_store_code === code || 
      s.wintouch_store_code?.toLowerCase() === code?.toLowerCase()
    ) || null;
  }, [storeMapping]);

  /**
   * Get store code from SAGE warehouse code
   * @param {string} sageWarehouse - SAGE warehouse code (e.g., "012")
   * @returns {string|null} - WinTouch code or null
   */
  const getCodeFromSageWarehouse = useCallback((sageWarehouse) => {
    if (!sageWarehouse) return null;
    const store = storeMapping.find(s => s.sage_warehouse === sageWarehouse);
    return store?.wintouch_store_code || null;
  }, [storeMapping]);

  /**
   * Clear cache and reload mapping
   */
  const refreshMapping = useCallback(async () => {
    globalStoreMapping = null;
    loadingPromise = null;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/retail-analytics/stores`);
      const stores = res.data.mapping || [];
      globalStoreMapping = stores;
      setStoreMapping(stores);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    storeMapping,
    loading,
    error,
    getStoreName,
    getStoreInfo,
    getCodeFromSageWarehouse,
    refreshMapping,
  };
}

export default useStoreMapping;
